import React, { useEffect, useState } from 'react';
import MediaDropZone from './MediaDropZone';
import * as welcomeService from '../../services/welcome.service';

/**
 * Team365 → Content Pushes → Onboarding: Jenn's welcome video and the course
 * new members are pointed to (Jenn, 2026-10-27). The video plays on a new
 * member's dashboard welcome and on the "You're in" page after payment.
 */
export default function NewMemberWelcomeSettings() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    welcomeService.getWelcomeSettings().then(setS).catch(() => setMsg({ bad: true, text: 'Could not load the welcome settings.' }));
  }, []);

  const save = async (fields, okText) => {
    setSaving(true); setMsg(null);
    try { setS(await welcomeService.updateWelcomeSettings(fields)); setMsg({ text: okText }); }
    catch (err) { setMsg({ bad: true, text: err.response?.data?.error || 'Could not save this. Nothing was changed.' }); }
    finally { setSaving(false); }
  };

  if (!s) return msg ? <p className="text-sm text-red-600 mb-5">{msg.text}</p> : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
      <p className="text-sm font-semibold text-reset-brown mb-1">New member welcome</p>
      <p className="text-xs text-gray-500 mb-4">
        What a new member sees on the "You're in" page after paying and at the top of their dashboard for their first 30 days.
        Day 1 below is their welcome email; it goes out the moment they join, with their sign-in button. Day 2 onward go out at 8am Atlantic.
      </p>

      <p className="text-xs font-semibold text-reset-brown mb-2">Your welcome video</p>
      {s.videoUrl ? (
        <div className="mb-3">
          <div className={`relative overflow-hidden rounded-xl bg-black ${s.videoShape === 'portrait' ? 'aspect-[9/16] max-w-[220px]' : 'aspect-video max-w-md'}`}>
            <iframe src={s.videoUrl} title="Welcome video" className="absolute inset-0 w-full h-full" allowFullScreen />
          </div>
          <p className="text-xs text-gray-500 mt-1">{s.videoTitle || 'Video Library video'} · <button disabled={saving} onClick={() => save({ videoId: null }, 'Welcome video removed.')} className="underline hover:text-reset-brown">Remove</button></p>
        </div>
      ) : (
        <p className="text-xs text-gray-500 mb-2">No welcome video yet. Drop one in below (it also goes into the Video Library).</p>
      )}
      <MediaDropZone kind="video" videoTitle="Welcome to 365reset (new member video)"
        onUploaded={(videoId) => save({ videoId }, 'Welcome video saved. It can take a few minutes to finish processing before it plays.')} />

      <p className="text-xs font-semibold text-reset-brown mt-5 mb-2">Course they start with</p>
      <select
        value={s.courseId || ''}
        disabled={saving}
        onChange={e => save({ courseId: e.target.value || null }, 'Course saved.')}
        className="w-full max-w-md px-3 py-2 border border-gray-200 rounded-lg text-sm"
      >
        <option value="">No course (point them to the Learning Lab)</option>
        {s.courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
      </select>

      {msg && <p className={`text-xs mt-3 ${msg.bad ? 'text-red-600' : 'text-green-700'}`}>{msg.text}</p>}
    </div>
  );
}
